// Contact imports for the console (docs/contract.md, section 15, Imports). Shapes mirror
// crates/api-types/src/imports.rs.

import { queryOptions } from "@tanstack/react-query";
import { newTagPath, type TagPickerCategory } from "@vd/ui";
import { api } from "./client";

export type ImportStatus =
  | "staging"
  | "staged"
  | "cleaned"
  | "scrubbing"
  | "scrubbed"
  | "draft"
  | "mapped"
  | "previewed"
  | "committing"
  | "committed"
  | "partial"
  | "failed"
  | "rolling_back"
  | "rolled_back"
  | "abandoned";

export interface ImportCounts {
  rows: number;
  create: number;
  link: number;
  review: number;
  skip: number;
  created: number;
  linked: number;
  failed: number;
  rolled_back: number;
  kept: number;
}

export type TagChoice =
  | { crm_id: string; name: string }
  | { name: string; parent?: unknown; category?: TagPickerCategory | null };

/** A rep tag's full path for display: its category's path, then its name. */
export function tagChoicePath(t: TagChoice): string {
  return "crm_id" in t ? t.name : newTagPath(t.name, t.category);
}

export interface ImportBatch {
  id: string;
  code: string;
  user_id: string;
  user_name: string | null;
  campaign_id: string;
  target_campaign_id: string | null;
  campaign_path: string;
  session_id: string | null;
  file_name: string | null;
  size_bytes: number | null;
  status: ImportStatus;
  purpose: string | null;
  selected_tiers: ("ready" | "review" | "weak")[];
  list_mode: "now" | "later";
  user_tags: TagChoice[];
  system_tags: string[];
  counts: ImportCounts;
  preview_hash: string | null;
  error: string | null;
  can_roll_back: boolean;
  rollback_until: string | null;
  created_at: string;
  committed_at: string | null;
  rolled_back_at: string | null;
}

export interface ImportRow {
  row_no: number;
  sheet: string | null;
  source_row: number | null;
  normalized: {
    name?: string | null;
    company?: string | null;
    phone?: string | null;
    mobile?: string | null;
    email?: string | null;
    website?: string | null;
    city?: string | null;
    state?: string | null;
  };
  issues: { field: string; code: string; message: string }[];
  match: { ref: string; reason: string; score: number; name: string | null } | null;
  decision: "create" | "link" | "review" | "skip";
  skip_reason: string | null;
  tier: "ready" | "review" | "weak" | null;
  status: "pending" | "done" | "failed" | "rolled_back" | "kept";
  crm_ref: string | null;
  error: string | null;
}

export interface ImportDetail {
  batch: ImportBatch;
  rows: ImportRow[];
  total: number;
}

export const ik = {
  list: ["admin", "imports"] as const,
  detail: (id: string, rows: string, page: number) => ["admin", "import", id, rows, page] as const,
};

export const importsApi = {
  list: () => api<{ items: ImportBatch[] }>("/v1/admin/imports"),
  detail: (id: string, rows: string, page: number) =>
    api<ImportDetail>(`/v1/admin/imports/${id}`, { query: { rows, page } }),
  rollback: (id: string) =>
    api<ImportBatch>(`/v1/admin/imports/${id}/rollback`, { method: "POST", body: {} }),
  file: (id: string) => api<{ url: string; expires_at: string }>(`/v1/imports/${id}/file`),
};

export const importsQuery = queryOptions({
  queryKey: ik.list,
  queryFn: importsApi.list,
  refetchInterval: (q) =>
    q.state.data?.items.some((b) => b.status === "committing" || b.status === "rolling_back") ? 3000 : false,
});

export const importQuery = (id: string, rows: string, page: number) =>
  queryOptions({ queryKey: ik.detail(id, rows, page), queryFn: () => importsApi.detail(id, rows, page) });

const STATUS: Record<ImportStatus, { label: string; tone: "neutral" | "ok" | "warn" | "danger" | "info" }> = {
  staging: { label: "Reading", tone: "neutral" },
  staged: { label: "In progress", tone: "neutral" },
  cleaned: { label: "In progress", tone: "neutral" },
  scrubbing: { label: "Checking", tone: "info" },
  scrubbed: { label: "In progress", tone: "neutral" },
  draft: { label: "In progress", tone: "neutral" },
  mapped: { label: "In progress", tone: "neutral" },
  previewed: { label: "Ready to import", tone: "info" },
  committing: { label: "Importing", tone: "info" },
  committed: { label: "Imported", tone: "ok" },
  partial: { label: "Partly imported", tone: "warn" },
  failed: { label: "Failed", tone: "danger" },
  rolling_back: { label: "Rolling back", tone: "info" },
  rolled_back: { label: "Rolled back", tone: "neutral" },
  abandoned: { label: "Abandoned", tone: "neutral" },
};

export function statusOf(s: ImportStatus) {
  return STATUS[s];
}

/** An import that wrote to Odoo, which an admin can roll back at any time. */
export function reachedCrm(b: ImportBatch): boolean {
  return (
    b.status === "committed" ||
    b.status === "partial" ||
    (b.status === "failed" && b.counts.created + b.counts.linked > 0)
  );
}
